import { DRAWER_BODY_RESET } from './ui/drawerOverrides';
import React, { useState, useEffect } from 'react';
import { Drawer, SIZE, ANCHOR } from 'baseui/drawer';
import {
  X, ArrowLeft, Plus, Save, Trash2, Check, Info, ChevronDown,
  Clock, Building2, Ticket, Package, Repeat, User, Users, Box, Armchair, Coffee,
  Sun, Calendar, CalendarDays,
} from 'lucide-react';
import { useCreateProduct, useUpdateProduct, useDeleteProduct, type Product } from '../hooks/useProducts';
import { businessHasCarnetConfigured, type Business } from '../hooks/useBusinessAccessType';
import { useAllBusinessesCrud } from '../hooks/useBusinessCrud';
import { PRODUCT_ICONS, findProductIcon } from '../utils/productIcons';
import { S3ImageUpload } from './S3ImageUpload';
import { OfferPickerGrid } from './offer/OfferPickerGrid';
import './product/productWizard.css';
import { useFdCan } from '../hooks/useFdCan';
import { useUserPermissions } from '../hooks/useUserPermissions';

/* ---------------- Catalogue ---------------- */
type IconC = React.ComponentType<any>;
// SAM-545 (N10) — "una sola entidad service y un solo wizard" (Abel,
// SAM-537, 2026-09-18): bookable/reservable offers (appointments, rentals,
// tickets — anything with a schedule) are created and edited as `service`
// through the Services tab (ServiceDetailDrawer) from here on, including
// the SAM-533 subtypes (Cabana, Minimum Consumption, Tee Time).
//
// SAM-198 (Confluence "12.4 Services admin UI — current vs proposed",
// adopted by Abel 2026-09-17): "the Products and Memberships cards still
// invite admins to build rows the app cannot book — removing them from the
// picker is the cheapest guard." SAM 2.0 scopes the booking wizard to
// services only (appointment/rental/ticket), and those were already routed
// to the Services tab by SAM-545 — Products and Memberships are the other
// two archetypes still creatable from THIS picker, and neither is
// reservable by the app, so both are retired from the "new offer" screen
// here too. `OFFER_TYPES` itself stays intact (used verbatim in edit mode
// via LEGACY_OFFER_TYPES) so a product/subscription/appointment/rental/
// ticket row saved before this change keeps opening/editing exactly as
// before — "existing product rows stay visible/editable, no data
// migration."
const OFFER_TYPES: Array<{ k: string; n: string; d: string; Icon: IconC }> = [
  { k: 'appointment', n: 'Appointments', d: 'Services booked by time', Icon: Clock },
  { k: 'rental', n: 'Rentals & Spaces', d: 'Stays, equipment, tables', Icon: Building2 },
  { k: 'ticket', n: 'Access & Tickets', d: 'Day passes, admission & events', Icon: Ticket },
  { k: 'product', n: 'Products', d: 'Goods or food & beverage', Icon: Package },
  { k: 'subscription', n: 'Memberships', d: 'Recurring plans & bundles', Icon: Repeat },
];
// Retired from the "new offer" picker (appointment/rental/ticket by SAM-545, product/subscription
// by SAM-198) but still rendered when editing an existing offer saved under one of these — no
// backfill exists for any of the five.
const LEGACY_OFFER_TYPES = new Set(['appointment', 'rental', 'ticket', 'product', 'subscription']);
// Nothing is creatable from this picker any more — every archetype it used to offer is now either
// reservable (-> Services) or retired outright (Products/Memberships, SAM-198). `OFFER_TYPES`
// itself stays intact (used verbatim in edit mode via LEGACY_OFFER_TYPES).
const CREATABLE_OFFER_TYPES = OFFER_TYPES.filter((o) => !LEGACY_OFFER_TYPES.has(o.k));
const OFFER_SUBTYPES: Record<string, Array<{ k: string; n: string; d: string; Icon: IconC }>> = {
  appointment: [
    { k: 'private', n: 'Appointments (1-on-1)', d: "Provider's schedule", Icon: User },
    { k: 'class', n: 'Classes (Groups)', d: 'Fixed recurring schedule', Icon: Users },
  ],
  rental: [
    { k: 'stay', n: 'Stays / Multi-Day', d: 'Villas, retreats, accommodations', Icon: Building2 },
    { k: 'equipment', n: 'Spaces & Equipment', d: 'By the hour or session', Icon: Box },
    { k: 'tables', n: 'Tables / Seating', d: 'Restaurant tables by party size', Icon: Armchair },
  ],
  // 'recurring' is intentionally absent: the design retired the card but kept the
  // behaviour, so offers already saved as recurring keep working (see legacy
  // branches in applySubtypeDefaults / the availability section).
  ticket: [
    { k: 'daypass', n: 'Access', d: 'Day pass or timed entry to the venue', Icon: Sun },
    { k: 'single', n: 'Single-day Event', d: 'One date, set start time', Icon: Calendar },
    { k: 'multiday', n: 'Multi-day Event', d: 'Spans several days — festivals, conferences', Icon: CalendarDays },
  ],
  product: [
    { k: 'physical', n: 'Physical Goods', d: 'Merch, retail, shipping', Icon: Package },
    { k: 'food', n: 'Food & Beverage', d: 'Menus, pickup, delivery', Icon: Coffee },
  ],
  subscription: [
    { k: 'membership', n: 'Memberships / Subscriptions', d: 'Recurring billing cycle', Icon: User },
    { k: 'bundle', n: 'Bundles / Packages', d: 'Consumption-based usage', Icon: Package },
  ],
};
// Subtypes retired from the picker but still present on saved offers — keeps the
// UI from falling back to the raw key for records created before the rework.
const LEGACY_SUBTYPE_NAMES: Record<string, string> = { recurring: 'Recurring Tours (legacy)' };
const subtypeName = (type: string, subtype: string) =>
  OFFER_SUBTYPES[type]?.find((s) => s.k === subtype)?.n || LEGACY_SUBTYPE_NAMES[subtype] || subtype;

const BILLING: Array<[string, string]> = [['monthly', 'Monthly'], ['quarterly', 'Quarterly'], ['yearly', 'Yearly']];

const paradigm = (t: string) =>
  (({ appointment: 'TIME_BASED', rental: 'ASSET_SPACE', ticket: 'EVENT_ACCESS', product: 'PRODUCT', subscription: 'SUBSCRIPTION' } as Record<string, string>)[t]);
const TYPE_LABELS: Record<string, string> = {
  appointment: 'Appointment', rental: 'Rental', ticket: 'Ticket', product: 'Product', subscription: 'Membership',
};
// Access/Event read better than the generic "Ticket" for the reworked ticket subtypes.
const typeLabel = (t: string, subtype?: string) => {
  if (subtype === 'daypass') return 'Access';
  if (subtype === 'single' || subtype === 'multiday') return 'Event';
  return TYPE_LABELS[t] || 'Product';
};
function offerSecs(type: string) {
  const par = paradigm(type);
  const s: Array<{ k: string; n: string }> = [{ k: 'details', n: 'Basic Details' }];
  if (par !== 'PRODUCT' && par !== 'SUBSCRIPTION') s.push({ k: 'avail', n: 'Scheduling & Capacity' });
  s.push({ k: 'pricing', n: par === 'SUBSCRIPTION' ? 'Pricing & Plans' : 'Pricing & Inventory' });
  if (par !== 'PRODUCT') s.push({ k: 'booking', n: 'Policies & Add-Ons' });
  return s;
}

/* ---------------- Form shape ---------------- */
interface BL { en: string; es: string }
/** min/max bound how many of this guest type may be in ONE booking (not total capacity). */
interface Tier { label: BL; price: string; min?: string; max?: string }
interface OfferForm {
  type: string; subtype: string;
  name: BL; sdesc: BL; desc: BL;
  includes: string; bring: string;
  details: { location: string; staff: string; intake: string; checkIn: string; checkOut: string };
  location: { name: string; city: string; postal: string };
  currency: 'DOP' | 'USD';
  charge: { unit: string; amount: string; includedGuests: string; tiers: Tier[]; extraFees: Array<{ label: string; amount: string }> };
  avail: { session: string; capacity: string; inventory: string; minDays: string; maxDays: string; bufferBefore: string; bufferAfter: string; duration_type: string; session_unit: string; qr: boolean };
  tables: Array<{ name: string; seats: string }>;
  plans: Array<{ name: string; price: string; billing: string }>;
  booking: { approval: string; payReq: string; payAmt: string; payMethod: string; cancel: string; age: string; terms: string; waitlist: boolean; minNotice: string; minNoticeUnit: string; maxAdvance: string; maxAdvanceUnit: string };
  gateway: 'portal' | 'carnet'; inventory: string; promo: boolean; active: boolean;
  icon_name: string; coverImage: string;
}

const blEmpty = (): BL => ({ en: '', es: '' });
const asBL = (v: any): BL => (v && typeof v === 'object') ? { en: v.en || '', es: v.es || '' } : { en: '', es: String(v || '') };
const symOf = (c: string) => (c === 'USD' ? '$' : 'RD$');

function offerDefaults(type: string): OfferForm {
  return {
    type, subtype: '',
    name: blEmpty(), sdesc: blEmpty(), desc: blEmpty(),
    includes: '', bring: '',
    details: { location: 'physical', staff: '', intake: '', checkIn: '', checkOut: '' },
    location: { name: '', city: '', postal: '' },
    currency: 'DOP',
    charge: { unit: type === 'product' ? 'item' : 'flat', amount: '', includedGuests: '5', tiers: [], extraFees: [] },
    avail: { session: '60', capacity: '1', inventory: '1', minDays: '1', maxDays: '30', bufferBefore: '0', bufferAfter: '15', duration_type: 'hourly', session_unit: 'hours', qr: true },
    tables: [],
    plans: [{ name: 'Monthly', price: '', billing: 'monthly' }],
    booking: { approval: 'instant', payReq: 'free', payAmt: '', payMethod: 'cc_auth', cancel: 'flexible', age: '', terms: '', waitlist: false, minNotice: '1', minNoticeUnit: 'hours', maxAdvance: '30', maxAdvanceUnit: 'days' },
    gateway: 'portal', inventory: '', promo: false, active: false,
    icon_name: '', coverImage: '',
  };
}
function applySubtypeDefaults(f: OfferForm) {
  const st = f.subtype, ch = f.charge, av = f.avail;
  if (st === 'stay') { ch.unit = 'night'; av.minDays = '1'; av.maxDays = '30'; av.inventory = '1'; av.capacity = '2'; }
  else if (st === 'equipment') { ch.unit = 'hour'; av.session = '60'; av.bufferAfter = '15'; av.inventory = '5'; av.capacity = '1'; }
  else if (st === 'private') { ch.unit = 'flat'; av.session = '45'; av.bufferAfter = '10'; av.capacity = '1'; }
  else if (st === 'class') { ch.unit = 'person'; av.session = '60'; av.capacity = '20'; ch.tiers = [{ label: { en: 'Standard', es: 'Estándar' }, price: '' }]; }
  else if (st === 'single') { ch.unit = 'person'; av.capacity = '100'; av.session = '120'; ch.tiers = [{ label: { en: 'General', es: 'General' }, price: '' }]; }
  else if (st === 'daypass') {
    ch.unit = 'person'; av.capacity = '200'; av.duration_type = 'fullday'; av.session = '6'; av.session_unit = 'hours'; av.qr = true;
    ch.tiers = [
      { label: { en: 'Adult', es: 'Adulto' }, price: '', min: '1', max: '6' },
      { label: { en: 'Kid', es: 'Niño' }, price: '', min: '0', max: '6' },
    ];
  }
  else if (st === 'multiday') { ch.unit = 'person'; av.capacity = '100'; av.qr = true; ch.tiers = [{ label: { en: 'General', es: 'General' }, price: '' }]; }
  // Legacy: no longer offered as a card, kept so existing offers still load.
  else if (st === 'recurring') { ch.unit = 'person'; av.capacity = '20'; av.session = '60'; ch.tiers = [{ label: { en: 'General', es: 'General' }, price: '' }]; }
  else if (st === 'tables') { ch.unit = 'flat'; av.session = '90'; av.bufferAfter = '15'; av.capacity = '2'; f.tables = [{ name: 'Table 1', seats: '2' }, { name: 'Table 2', seats: '4' }, { name: 'Table 3', seats: '6' }]; f.booking.payReq = 'min_consume'; }
  else if (st === 'physical' || st === 'food') { ch.unit = 'item'; }
  else if (st === 'membership') { f.plans = [{ name: 'Monthly', price: '', billing: 'monthly' }]; }
  else if (st === 'bundle') { ch.unit = 'flat'; }
}
function unitOptions(f: OfferForm): Array<[string, string]> {
  const st = f.subtype;
  if (st === 'private') return [['flat', 'Flat rate (per slot)'], ['person', 'Price per person']];
  if (st === 'daypass') return [['person', 'Price per guest type']];
  if (st === 'class') return [['person', 'Price per person']];
  if (st === 'stay') return [['night', 'Per night'], ['day', 'Per day']];
  if (st === 'equipment') return [['hour', 'Per hour'], ['session', 'Per session'], ['base+extra', 'Base + extra']];
  if (st === 'tables') return [['flat', 'Free reservation'], ['person', 'Cover charge / person']];
  return [['item', 'Per item'], ['person', 'Per person'], ['flat', 'Flat rate']];
}
function priceFilled(f: OfferForm): boolean {
  const ch = f.charge;
  if (f.type === 'subscription') return (f.plans || []).some((p) => String(p.price).trim() !== '');
  if (ch.unit === 'person' || ch.unit === 'base+extra') {
    const t = (ch.tiers || []).some((x) => String(x.price).trim() !== '');
    return ch.unit === 'base+extra' ? (t || String(ch.amount).trim() !== '') : t;
  }
  if (f.subtype === 'tables' && ch.unit === 'flat') return true;
  return String(ch.amount).trim() !== '';
}

/* ---------------- Product <-> form mapping ---------------- */
const splitCsv = (s: string): string[] => String(s || '').split(',').map((x) => x.trim()).filter(Boolean);
const numOrU = (s: any): number | undefined => { const n = Number(s); return (s !== '' && s != null && !Number.isNaN(n)) ? n : undefined; };
const tiersToBookingOptions = (tiers: Tier[], currency: string) =>
  tiers.filter((t) => String(t.price).trim() !== '').map((t, i) => {
    const en = t.label?.en || '', es = t.label?.es || '';
    return {
      id: (en || es).toLowerCase().replace(/\s+/g, '_') || `tier_${i}`,
      label_en: en || es, label_es: es || en,
      price: Number(t.price) || 0, currency,
      min_qty: numOrU(t.min), max_qty: numOrU(t.max),
    };
  });
const bookingOptionsToTiers = (
  opts?: Array<{ label_en?: string; label_es?: string; price?: number; min_qty?: number; max_qty?: number }>,
): Tier[] =>
  (opts || []).map((o) => ({
    label: { en: o.label_en || o.label_es || '', es: o.label_es || o.label_en || '' },
    price: o.price != null ? String(o.price) : '',
    min: o.min_qty != null ? String(o.min_qty) : '',
    max: o.max_qty != null ? String(o.max_qty) : '',
  }));

function offerPriceValue(f: OfferForm): string {
  if (f.type === 'subscription') return f.plans.find((p) => String(p.price).trim() !== '')?.price || '';
  if (f.charge.unit === 'person' || f.charge.unit === 'base+extra') {
    const t = f.charge.tiers.find((x) => String(x.price).trim() !== '');
    if (f.charge.unit === 'base+extra') return f.charge.amount || t?.price || '';
    return t?.price || '';
  }
  return f.charge.amount || '';
}

function productToForm(product: Product | null): OfferForm {
  const f = offerDefaults(product?.offer_type || 'product');
  if (!product) return f;

  if (product.offer_type) {
    // New schema — rebuild from first-class columns.
    f.type = product.offer_type;
    f.subtype = product.offer_subtype || OFFER_SUBTYPES[f.type]?.[0]?.k || '';
    f.name = { en: product.name_en || '', es: product.name_es || product.name || '' };
    f.sdesc = { en: product.short_description_en || '', es: product.short_description_es || '' };
    f.desc = { en: product.description_en || '', es: product.description_es || product.description || '' };
    f.includes = (product.includes || []).join(', ');
    f.bring = (product.bring || []).join(', ');
    f.currency = product.currency === 'USD' ? 'USD' : 'DOP';
    f.charge.unit = product.pricing_unit || f.charge.unit;
    f.charge.amount = product.price != null ? String(product.price) : '';
    f.charge.includedGuests = product.included_guests != null ? String(product.included_guests) : '5';
    f.charge.tiers = bookingOptionsToTiers(product.booking_options as any);
    f.charge.extraFees = (product.extra_fees as any) || [];
    f.plans = (product.plans as any)?.length ? (product.plans as any) : f.plans;
    f.tables = (product.tables as any) || [];
    f.location = { name: product.location?.name || '', city: product.location?.city || '', postal: product.location?.postal || '' };
    const d = product.details || {};
    f.details = { location: d.meeting_type || 'physical', staff: d.staff || '', intake: d.intake || '', checkIn: d.check_in || '', checkOut: d.check_out || '' };
    f.avail.capacity = product.max_capacity != null ? String(product.max_capacity) : f.avail.capacity;
    f.avail.session = product.duration != null ? String(product.duration) : f.avail.session;
    f.avail.inventory = product.inventory != null ? String(product.inventory) : f.avail.inventory;
    f.avail.duration_type = product.duration_type || f.avail.duration_type;
    f.avail.qr = product.qr_enabled !== false;
    if (d.min_days != null) f.avail.minDays = String(d.min_days);
    if (d.max_days != null) f.avail.maxDays = String(d.max_days);
    if (d.buffer_before != null) f.avail.bufferBefore = String(d.buffer_before);
    if (d.buffer_after != null) f.avail.bufferAfter = String(d.buffer_after);
    const bp = product.booking_policy || {};
    f.booking = {
      approval: bp.approval || 'instant', payReq: bp.payment_requirement || 'free', payAmt: bp.payment_amount || '',
      payMethod: bp.payment_method || 'cc_auth', cancel: bp.cancellation || 'flexible',
      age: bp.age_restriction || (product.restrictions?.min_age != null ? String(product.restrictions.min_age) : ''),
      terms: bp.terms || '', waitlist: !!bp.waitlist,
      minNotice: product.booking_window_start || '1', minNoticeUnit: bp.min_notice_unit || 'hours',
      maxAdvance: product.booking_window_end || '30', maxAdvanceUnit: bp.max_advance_unit || 'days',
    };
    f.gateway = product.payment_gateway === 'carnet' ? 'carnet' : 'portal';
    f.inventory = product.inventory != null ? String(product.inventory) : '';
    f.promo = !!product.allow_promo;
    f.active = !!product.is_active;
    f.icon_name = product.icon_name || '';
    f.coverImage = product.images?.[0] || '';
    return f;
  }

  // Legacy fallback: attribute blob, then flat fields.
  if (product.attribute) {
    try { const saved = JSON.parse(product.attribute); if (saved?.__offerWizard && saved.form) return { ...offerDefaults('product'), ...saved.form }; } catch { /* ignore */ }
  }
  f.subtype = OFFER_SUBTYPES.product[0].k;
  f.name = asBL(product.name);
  f.desc = asBL(product.description);
  f.charge.amount = product.price != null ? String(product.price) : '';
  f.gateway = product.payment_gateway === 'carnet' ? 'carnet' : 'portal';
  f.icon_name = product.icon_name || '';
  f.coverImage = product.images?.[0] || '';
  f.active = true;
  return f;
}

function formToProductData(f: OfferForm): Partial<Product> {
  const priceStr = offerPriceValue(f);
  return {
    name: (f.name.es || f.name.en || '').trim(),
    description: (f.desc.es || f.desc.en || '').trim() || undefined,
    name_en: f.name.en || undefined,
    name_es: f.name.es || undefined,
    short_description_en: f.sdesc.en || undefined,
    short_description_es: f.sdesc.es || undefined,
    description_en: f.desc.en || undefined,
    description_es: f.desc.es || undefined,
    offer_type: f.type,
    offer_subtype: f.subtype || undefined,
    currency: f.currency,
    pricing_unit: f.charge.unit,
    price: priceStr.trim() !== '' ? Number(priceStr) : undefined,
    included_guests: numOrU(f.charge.includedGuests),
    inventory: numOrU(f.inventory) ?? numOrU(f.avail.inventory),
    payment_gateway: f.gateway,
    allow_promo: f.promo,
    booking_options: f.charge.tiers.length ? tiersToBookingOptions(f.charge.tiers, f.currency) : undefined,
    extra_fees: f.charge.extraFees.length ? f.charge.extraFees : undefined,
    plans: f.type === 'subscription' ? f.plans : undefined,
    max_capacity: numOrU(f.avail.capacity),
    duration: f.avail.session || undefined,
    duration_type: f.avail.duration_type || undefined,
    qr_enabled: f.avail.qr,
    booking_window_start: f.booking.minNotice || undefined,
    booking_window_end: f.booking.maxAdvance || undefined,
    tables: f.tables.length ? f.tables : undefined,
    location: (f.location.name || f.location.city || f.location.postal) ? f.location : undefined,
    details: {
      meeting_type: f.details.location, staff: f.details.staff, intake: f.details.intake,
      check_in: f.details.checkIn, check_out: f.details.checkOut,
      min_days: numOrU(f.avail.minDays), max_days: numOrU(f.avail.maxDays),
      buffer_before: numOrU(f.avail.bufferBefore), buffer_after: numOrU(f.avail.bufferAfter),
    },
    includes: splitCsv(f.includes),
    bring: splitCsv(f.bring),
    restrictions: f.booking.age ? { min_age: numOrU(f.booking.age) } : undefined,
    booking_policy: {
      approval: f.booking.approval, payment_requirement: f.booking.payReq, payment_amount: f.booking.payAmt,
      payment_method: f.booking.payMethod, cancellation: f.booking.cancel, age_restriction: f.booking.age,
      terms: f.booking.terms, waitlist: f.booking.waitlist,
      min_notice_unit: f.booking.minNoticeUnit, max_advance_unit: f.booking.maxAdvanceUnit,
    },
    is_active: f.active,
    icon_name: f.icon_name || undefined,
    images: f.coverImage ? [f.coverImage] : undefined,
  };
}

/* ---------------- Tag / chip input ---------------- */
const TagInput: React.FC<{ value: string; onChange: (v: string) => void; placeholder?: string }> = ({ value, onChange, placeholder }) => {
  const [draft, setDraft] = useState('');
  const ref = React.useRef<HTMLInputElement>(null);
  const tags = (value || '').split(',').map((s) => s.trim()).filter(Boolean);

  const commit = (raw: string) => {
    const parts = raw.split(',').map((s) => s.trim()).filter(Boolean);
    if (!parts.length) { setDraft(''); return; }
    const next = [...tags];
    parts.forEach((p) => { if (!next.some((t) => t.toLowerCase() === p.toLowerCase())) next.push(p); });
    onChange(next.join(', '));
    setDraft('');
  };
  const removeAt = (i: number) => onChange(tags.filter((_, x) => x !== i).join(', '));

  return (
    <div className="tags-input" onClick={() => ref.current?.focus()}>
      {tags.map((t, i) => (
        <span className="tag-chip" key={`${t}-${i}`}>
          {t}
          <button type="button" onClick={(e) => { e.stopPropagation(); removeAt(i); }} aria-label={`Remove ${t}`}><X size={12} /></button>
        </span>
      ))}
      <input
        ref={ref}
        className="tags-field"
        value={draft}
        placeholder={tags.length ? '' : (placeholder || '')}
        onChange={(e) => { const v = e.target.value; if (v.includes(',')) commit(v); else setDraft(v); }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(draft); }
          else if (e.key === 'Backspace' && !draft && tags.length) { e.preventDefault(); removeAt(tags.length - 1); }
        }}
        onBlur={() => commit(draft)}
      />
    </div>
  );
};

/* ---------------- Searchable icon picker ---------------- */
const IconPicker: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const selected = value ? findProductIcon(value) : undefined;
  const SelIcon = selected?.lucide;
  const filtered = PRODUCT_ICONS.filter((i) => i.label.toLowerCase().includes(query.trim().toLowerCase())).slice(0, 60);

  return (
    <div className="icon-picker">
      <div className="icon-picker-control">
        <span className="icon-picker-current">{SelIcon ? <SelIcon size={18} /> : <span className="muted">—</span>}</span>
        <input
          className="icon-picker-input"
          value={open ? query : (selected?.label || '')}
          placeholder={selected ? selected.label : 'Search an icon…'}
          onFocus={() => { setOpen(true); setQuery(''); }}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onBlur={() => setTimeout(() => setOpen(false), 130)}
          onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
        />
        {value && (
          <button type="button" className="icon-picker-clear" onMouseDown={(e) => { e.preventDefault(); onChange(''); setQuery(''); }} aria-label="Clear icon">
            <X size={14} />
          </button>
        )}
        <ChevronDown size={15} className="icon-picker-caret" />
      </div>
      {open && (
        <div className="icon-picker-menu">
          {filtered.map((i) => {
            const I = i.lucide;
            return (
              <button
                type="button"
                key={i.sfSymbol}
                className={`icon-picker-opt ${i.sfSymbol === value ? 'sel' : ''}`}
                onMouseDown={(e) => { e.preventDefault(); onChange(i.sfSymbol); setOpen(false); setQuery(''); }}
              >
                <I size={18} /><span>{i.label}</span>
              </button>
            );
          })}
          {!filtered.length && <div className="icon-picker-empty">No icons match.</div>}
        </div>
      )}
    </div>
  );
};

/* ---------------- Component ---------------- */
interface ProductDetailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  product: Product | null;
  mode: 'create' | 'edit';
  businessId: string | null;
  businesses?: Business[];
  isAdmin?: boolean;
  hideBusinessSelector?: boolean;
}

export const ProductDetailDrawer: React.FC<ProductDetailDrawerProps> = ({
  isOpen, onClose, product, mode, businessId, businesses = [], isAdmin = false, hideBusinessSelector = false,
}) => {
  const createProduct = useCreateProduct();
  const updateProduct = useUpdateProduct();
  const deleteProduct = useDeleteProduct();

  // The tenant Front Desk views the catalogue but does not change it (specs/front-desk-tenant-permisos.md).
  const canEditProduct = useFdCan('business', 'edit');
  // S-78: the gateway of a product is Xhinola's alone; isAdmin also covers the tenant admin.
  const { isXhinolaAdmin } = useUserPermissions();
  const [phase, setPhase] = useState<'type' | 'subtype' | 'form'>('type');
  const [sec, setSec] = useState<string>('details');
  const [lang, setLang] = useState<'es' | 'en'>('es');
  const [form, setForm] = useState<OfferForm>(() => offerDefaults('product'));
  const [errors, setErrors] = useState<{ name?: string; price?: string }>({});
  const [removeArmed, setRemoveArmed] = useState(false);
  const [targetBiz, setTargetBiz] = useState<string>('');

  useEffect(() => {
    if (mode === 'edit' && product) {
      setForm(productToForm(product));
      setPhase('form');
      setTargetBiz(product.business_id || businessId || '');
    } else {
      setForm(offerDefaults('product'));
      setPhase('type');
      setTargetBiz(businessId || '');
    }
    setSec('details');
    setLang('es');
    setErrors({});
    setRemoveArmed(false);
  }, [product, mode, isOpen, businessId]);

  /* immutable deep set by dotted path */
  const set = (path: string, value: any) => setForm((prev) => {
    const next: any = structuredClone(prev);
    const ks = path.split('.'); const last = ks.pop() as string;
    let t = next;
    ks.forEach((k) => { if (t[k] == null) t[k] = {}; t = t[k]; });
    t[last] = value;
    return next;
  });
  const setBL = (path: 'name' | 'sdesc' | 'desc', value: string) =>
    setForm((prev) => ({ ...prev, [path]: { ...prev[path], [lang]: value } }));
  const getP = (path: string): any => path.split('.').reduce((a: any, k) => (a == null ? a : a[k]), form as any);

  const pickType = (k: string) => {
    setForm((prev) => {
      // Preserve cross-type fields so changing the type on edit doesn't wipe copy/media.
      const f = offerDefaults(k);
      f.name = prev.name; f.sdesc = prev.sdesc; f.desc = prev.desc;
      f.includes = prev.includes; f.bring = prev.bring;
      f.icon_name = prev.icon_name; f.coverImage = prev.coverImage;
      f.currency = prev.currency; f.gateway = prev.gateway;
      f.active = prev.active; f.promo = prev.promo;
      return f;
    });
    setPhase('subtype');
  };
  const pickSubtype = (k: string) => {
    setForm((prev) => { const f = structuredClone(prev); f.subtype = k; applySubtypeDefaults(f); return f; });
    setSec('details'); setPhase('form');
  };

  /* repeaters */
  const tiers = form.charge.tiers, fees = form.charge.extraFees, plans = form.plans, tables = form.tables;
  const addTier = () => set('charge.tiers', [...tiers, { label: blEmpty(), price: '' }]);
  const rmTier = (i: number) => set('charge.tiers', tiers.filter((_, x) => x !== i));
  const addFee = () => set('charge.extraFees', [...fees, { label: '', amount: '' }]);
  const rmFee = (i: number) => set('charge.extraFees', fees.filter((_, x) => x !== i));
  const addPlan = () => set('plans', [...plans, { name: '', price: '', billing: 'monthly' }]);
  const rmPlan = (i: number) => set('plans', plans.filter((_, x) => x !== i));
  const addTable = () => set('tables', [...tables, { name: 'Table ' + (tables.length + 1), seats: '2' }]);
  const rmTable = (i: number) => set('tables', tables.filter((_, x) => x !== i));

  /* carnet availability */
  const { data: allBusinesses } = useAllBusinessesCrud();
  const lookup = businesses.length > 0 ? businesses : (allBusinesses || []);
  const carnetEnabled = businessHasCarnetConfigured((lookup.find((b) => b.id === (targetBiz || businessId)) as any)?.settings);

  const validate = (): boolean => {
    const e: { name?: string; price?: string } = {};
    if (!(form.name.es || form.name.en || '').trim()) e.name = 'Name is required';
    if (!priceFilled(form)) e.price = 'Set a price';
    setErrors(e);
    if (e.name) setSec('details'); else if (e.price) setSec('pricing');
    return Object.keys(e).length === 0;
  };
  const handleSave = () => {
    if (!validate()) return;
    const data = formToProductData(form);
    // Update is a PATCH: leaving the key out keeps the stored gateway; a create takes the server default.
    if (!isXhinolaAdmin) delete data.payment_gateway;
    if (mode === 'create') {
      (data as any).business_id = targetBiz || businessId || undefined;
      createProduct.mutate(data, { onSuccess: () => onClose() });
    } else if (product) {
      updateProduct.mutate({ id: product.id, data }, { onSuccess: () => onClose() });
    }
  };
  const handleDelete = () => { if (product) deleteProduct.mutate(product.id, { onSuccess: () => onClose() }); };

  const isSaving = createProduct.isPending || updateProduct.isPending;
  const isDeleting = deleteProduct.isPending;

  /* header text per phase */
  const editingName = form.name.es || form.name.en || 'Offer';
  const header = phase === 'type'
    ? mode === 'edit'
      ? { t: editingName, s: 'Change type · choose a type' }
      : { t: 'New offer', s: 'Step 1 · choose a type' }
    : phase === 'subtype'
      ? mode === 'edit'
        ? { t: editingName, s: 'Change type · choose a setup' }
        : { t: 'New ' + typeLabel(form.type, form.subtype).toLowerCase(), s: 'Step 2 · choose a setup' }
      : mode === 'edit'
        ? { t: form.name.es || form.name.en || 'Offer', s: 'Edit offer' }
        : { t: 'New ' + typeLabel(form.type, form.subtype).toLowerCase(), s: (form.subtype ? subtypeName(form.type, form.subtype) : '') };

  /* ---- field helpers (JSX) ---- */
  const Fld = ({ label, path, ph, num }: { label: string; path: string; ph?: string; num?: boolean }) => (
    <div className="fc">
      <label>{label}</label>
      <input type={num ? 'number' : 'text'} value={getP(path) ?? ''} onChange={(e) => set(path, e.target.value)} placeholder={ph || ''} />
    </div>
  );
  const Sel = ({ label, path, opts }: { label: string; path: string; opts: Array<[string, string]> }) => (
    <div className="fc">
      <label>{label}</label>
      <select value={getP(path) ?? ''} onChange={(e) => set(path, e.target.value)}>
        {opts.map((o) => <option key={o[0]} value={o[0]}>{o[1]}</option>)}
      </select>
    </div>
  );
  const BlField = ({ label, path, ph, ta }: { label: string; path: 'name' | 'sdesc' | 'desc'; ph: string; ta?: boolean }) => {
    const isName = path === 'name';
    return (
      <div className={`fc ${isName && errors.name ? 'err' : ''}`}>
        <label>{label}{isName && <span className="req"> *</span>}</label>
        {ta
          ? <textarea value={form[path][lang]} onChange={(e) => setBL(path, e.target.value)} placeholder={ph} />
          : <input value={form[path][lang]} onChange={(e) => setBL(path, e.target.value)} placeholder={ph} />}
        {isName && <div className="msg">{errors.name || ''}</div>}
      </div>
    );
  };
  const Price = ({ path }: { path: string }) => (
    <div className="pwrap">
      <span className="sym">{symOf(form.currency)}</span>
      <input type="number" value={getP(path) ?? ''} onChange={(e) => set(path, e.target.value)} placeholder="0" />
    </div>
  );

  /* ---- sections ---- */
  const SecDetails = () => (
    <>
      <div className="bz-lang-tabs">
        <button type="button" className={`bz-lang-tab ${lang === 'es' ? 'is-active' : ''}`} onClick={() => setLang('es')}>Español</button>
        <button type="button" className={`bz-lang-tab ${lang === 'en' ? 'is-active' : ''}`} onClick={() => setLang('en')}>English</button>
      </div>
      {BlField({ label: "Name", path: "name", ph: lang === 'es' ? 'Nombre' : 'Name' })}
      {BlField({ label: "Short description", path: "sdesc", ph: lang === 'es' ? 'Resumen breve' : 'Short summary' })}
      {BlField({ label: "Full description", path: "desc", ph: lang === 'es' ? 'Descripción completa' : 'Full description', ta: true })}
      {form.type === 'appointment' && (
        <>
          <div className="bz-grid-2">
            {Sel({ label: "Location / meeting type", path: "details.location", opts: [['physical', 'Physical location'], ['video', 'Video call'], ['phone', 'Phone call']] })}
            {Fld({ label: "Provider / staff (optional)", path: "details.staff", ph: "Who provides this?" })}
          </div>
          {Fld({ label: "Pre-booking question (optional)", path: "details.intake", ph: "e.g. Briefly describe what you need" })}
        </>
      )}

      <div className="bz-section-title" style={{ margin: '20px 0 10px' }}>Media</div>
      <div className="fc">
        <label>Cover image</label>
        <S3ImageUpload variant="slot" value={form.coverImage} onChange={(k) => set('coverImage', k)} placeholder="Cover" slotWidth="100%" slotHeight="150px" />
      </div>
      <div className="fc">
        <label>App icon</label>
        <IconPicker value={form.icon_name} onChange={(v) => set('icon_name', v)} />
      </div>

      <div className="bz-section-title" style={{ margin: '20px 0 10px' }}>Highlights</div>
      <div className="fc">
        <label>Includes</label>
        <TagInput value={form.includes} onChange={(v) => set('includes', v)} placeholder="Type and press comma… e.g. Tax, Welcome drink" />
      </div>
      <div className="fc">
        <label>What to bring</label>
        <TagInput value={form.bring} onChange={(v) => set('bring', v)} placeholder="Type and press comma… e.g. ID, Towel" />
      </div>

      <div className="bz-section-title" style={{ margin: '20px 0 10px' }}>Visibility</div>
      <div className="status-seg">
        <button type="button" className={form.active ? 'on' : ''} onClick={() => set('active', true)}>Active</button>
        <button type="button" className={!form.active ? 'on' : ''} onClick={() => set('active', false)}>Draft</button>
      </div>
      <div className="cap" style={{ marginTop: 6, fontSize: 12, color: '#6B7280' }}>Draft offers stay hidden in the app until activated.</div>
    </>
  );

  const SecAvail = () => {
    const st = form.subtype;
    return (
      <>
        <div className="fc">
          <label>Location / venue</label>
          <input value={form.location.name} onChange={(e) => set('location.name', e.target.value)} placeholder="Venue name or address" />
          <div className="hintbox"><Info size={15} /><span>Timezone <b>America/Santo_Domingo</b> — used to generate bookable time slots.</span></div>
        </div>
        <div className="bz-grid-2">
          {Fld({ label: "City / area", path: "location.city", ph: "Cap Cana, Punta Cana" })}
          {Fld({ label: "Postal code", path: "location.postal", ph: "23000" })}
        </div>

        {st === 'tables' && (
          <>
            <div className="bz-grid-2">
              {Fld({ label: "Reservation length (mins)", path: "avail.session", ph: "90", num: true })}
              {Fld({ label: "Turn / cleanup (mins)", path: "avail.bufferAfter", ph: "15", num: true })}
            </div>
            <div className="bz-section-title" style={{ margin: '18px 0 8px' }}>Tables</div>
            {tables.map((tb, i) => (
              <div className="orow" key={i}>
                <input value={tb.name} onChange={(e) => set(`tables.${i}.name`, e.target.value)} placeholder="Table name" />
                <input type="number" value={tb.seats} onChange={(e) => set(`tables.${i}.seats`, e.target.value)} placeholder="Seats" />
                <button type="button" className="rm" onClick={() => rmTable(i)}>×</button>
              </div>
            ))}
            <button type="button" className="chip-add" onClick={addTable}>+ add table</button>
          </>
        )}
        {st === 'stay' && (
          <>
            <div className="bz-grid-2">{Fld({ label: "Min nights", path: "avail.minDays", ph: "1", num: true })}{Fld({ label: "Max nights", path: "avail.maxDays", ph: "30", num: true })}</div>
            <div className="bz-grid-2">{Fld({ label: "Check-in time", path: "details.checkIn", ph: "15:00" })}{Fld({ label: "Check-out time", path: "details.checkOut", ph: "11:00" })}</div>
            <div className="bz-grid-2">{Fld({ label: "Total units available", path: "avail.inventory", ph: "1", num: true })}{Fld({ label: "Max guests per unit", path: "avail.capacity", ph: "2", num: true })}</div>
          </>
        )}
        {st === 'equipment' && (
          <>
            {Sel({ label: "Duration type", path: "avail.duration_type", opts: [['hourly', 'Hourly (requires min hours)'], ['fullday', 'Full day / session']] })}
            {form.avail.duration_type === 'fullday'
              ? <div className="hintbox"><Info size={15} /><span>Operating hours follow the business schedule.</span></div>
              : <div className="bz-grid-2">{Fld({ label: "Buffer before (mins)", path: "avail.bufferBefore", ph: "0", num: true })}{Fld({ label: "Buffer after (mins)", path: "avail.bufferAfter", ph: "15", num: true })}</div>}
            <div className="bz-grid-2">{Fld({ label: "Total units available", path: "avail.inventory", ph: "5", num: true })}{Fld({ label: "Max pax per unit", path: "avail.capacity", ph: "1", num: true })}</div>
          </>
        )}
        {st === 'private' && (
          <>
            <div className="bz-grid-2">{Fld({ label: "Duration (mins)", path: "avail.session", ph: "45", num: true })}{Fld({ label: "Buffer after (mins)", path: "avail.bufferAfter", ph: "10", num: true })}</div>
            {Fld({ label: "Provider / staff (resource)", path: "details.staff", ph: "Leave blank to use business schedule" })}
          </>
        )}
        {st === 'class' && (
          <div className="bz-grid-2">{Fld({ label: "Duration (mins)", path: "avail.session", ph: "60", num: true })}{Fld({ label: "Max attendees", path: "avail.capacity", ph: "20", num: true })}</div>
        )}
        {st === 'single' && (
          <>
            <div className="bz-grid-2">{Fld({ label: "Event date & time", path: "details.checkIn", ph: "" })}{Fld({ label: "Duration (mins)", path: "avail.session", ph: "120", num: true })}</div>
            <div className="bz-grid-2">
              {Fld({ label: "Total tickets / capacity", path: "avail.capacity", ph: "100", num: true })}
              <div className="fc"><label>&nbsp;</label>
                <span className={`chk ${form.avail.qr ? 'on' : ''}`} onClick={() => set('avail.qr', !form.avail.qr)}><span className="box"><Check size={12} /></span>Enable QR tickets</span>
              </div>
            </div>
          </>
        )}
        {st === 'daypass' && (
          <>
            {Sel({ label: "Access duration", path: "avail.duration_type", opts: [['fullday', 'Full day — follows venue hours'], ['timed', 'Timed session']] })}
            {form.avail.duration_type === 'timed' ? (
              <div className="fc"><label>Session length</label>
                <div className="phone-group">
                  <input type="number" value={form.avail.session} onChange={(e) => set('avail.session', e.target.value)} style={{ flex: 1 }} />
                  <select value={form.avail.session_unit} onChange={(e) => set('avail.session_unit', e.target.value)} style={{ flex: '0 0 96px' }}>
                    {[['hours', 'Hours'], ['minutes', 'Minutes']].map((o) => <option key={o[0]} value={o[0]}>{o[1]}</option>)}
                  </select>
                </div>
              </div>
            ) : (
              <div className="hintbox"><Info size={14} /><span>Access is valid during the venue's open hours on the date of visit.</span></div>
            )}
            <div className="bz-grid-2">
              {Fld({ label: "Daily capacity (guests)", path: "avail.capacity", ph: "200", num: true })}
              <div className="fc"><label>&nbsp;</label>
                <span className={`chk ${form.avail.qr ? 'on' : ''}`} onClick={() => set('avail.qr', !form.avail.qr)}><span className="box"><Check size={12} /></span>Enable QR check-in</span>
              </div>
            </div>
          </>
        )}
        {st === 'multiday' && (
          <>
            <div className="bz-grid-2">{Fld({ label: "Start date", path: "details.checkIn", ph: "" })}{Fld({ label: "End date", path: "details.checkOut", ph: "" })}</div>
            <div className="bz-grid-2">
              {Fld({ label: "Total tickets / capacity", path: "avail.capacity", ph: "100", num: true })}
              <div className="fc"><label>&nbsp;</label>
                <span className={`chk ${form.avail.qr ? 'on' : ''}`} onClick={() => set('avail.qr', !form.avail.qr)}><span className="box"><Check size={12} /></span>Enable QR tickets</span>
              </div>
            </div>
          </>
        )}
        {/* Legacy subtype — retired from the picker, still editable on existing offers. */}
        {st === 'recurring' && (
          <div className="bz-grid-2">{Fld({ label: "Duration (mins)", path: "avail.session", ph: "60", num: true })}{Fld({ label: "Total tickets / capacity", path: "avail.capacity", ph: "20", num: true })}</div>
        )}

        <div className="bz-section-title" style={{ margin: '18px 0 8px' }}>Booking window</div>
        <div className="bz-grid-2">
          <div className="fc"><label>Min. advance notice</label>
            <div className="phone-group">
              <input type="number" value={form.booking.minNotice} onChange={(e) => set('booking.minNotice', e.target.value)} />
              <select value={form.booking.minNoticeUnit} onChange={(e) => set('booking.minNoticeUnit', e.target.value)}>
                {[['minutes', 'Minutes'], ['hours', 'Hours'], ['days', 'Days']].map((o) => <option key={o[0]} value={o[0]}>{o[1]}</option>)}
              </select>
            </div>
          </div>
          <div className="fc"><label>Max advance booking</label>
            <div className="phone-group">
              <input type="number" value={form.booking.maxAdvance} onChange={(e) => set('booking.maxAdvance', e.target.value)} />
              <select value={form.booking.maxAdvanceUnit} onChange={(e) => set('booking.maxAdvanceUnit', e.target.value)}>
                {[['days', 'Days'], ['weeks', 'Weeks'], ['months', 'Months']].map((o) => <option key={o[0]} value={o[0]}>{o[1]}</option>)}
              </select>
            </div>
          </div>
        </div>
      </>
    );
  };

  const SecPricing = () => {
    const sym = symOf(form.currency);
    const unit = form.charge.unit;
    return (
      <>
        <div className="bz-grid-2">
          {Sel({ label: "Currency", path: "currency", opts: [['DOP', 'DOP (RD$)'], ['USD', 'USD ($)']] })}
          {form.type !== 'subscription' && (
            <div className="fc"><label>Pricing model</label>
              <select value={unit} onChange={(e) => set('charge.unit', e.target.value)}>
                {unitOptions(form).map((o) => <option key={o[0]} value={o[0]}>{o[1]}</option>)}
              </select>
            </div>
          )}
        </div>

        {form.subtype === 'tables' && unit === 'flat' && (
          <div className="hintbox"><Info size={15} /><span>Most restaurants keep the reservation free and set a deposit or minimum consumption under <b>Policies</b>.</span></div>
        )}

        {form.type === 'subscription' ? (
          <div className={`fc ${errors.price ? 'err' : ''}`}>
            <label>Plans <span className="req">*</span></label>
            {plans.map((pk, i) => (
              <div key={i} style={{ border: '1px solid #E5E7EB', borderRadius: 10, padding: 10, marginBottom: 8 }}>
                <div className="orow" style={{ gridTemplateColumns: '1fr 32px' }}>
                  <input value={pk.name} onChange={(e) => set(`plans.${i}.name`, e.target.value)} placeholder="Plan name" />
                  <button type="button" className="rm" onClick={() => rmPlan(i)}>×</button>
                </div>
                <div className="bz-grid-2" style={{ marginTop: 8 }}>
                  <div className="pwrap"><span className="sym">{sym}</span><input type="number" value={pk.price} onChange={(e) => set(`plans.${i}.price`, e.target.value)} /></div>
                  <select value={pk.billing} onChange={(e) => set(`plans.${i}.billing`, e.target.value)}>
                    {BILLING.map((b) => <option key={b[0]} value={b[0]}>{b[1]}</option>)}
                  </select>
                </div>
              </div>
            ))}
            <button type="button" className="chip-add" onClick={addPlan}>+ plan</button>
            <div className="msg">{errors.price || ''}</div>
          </div>
        ) : (unit === 'person' || unit === 'base+extra') ? (
          <>
            {unit === 'base+extra' && (
              <div className="bz-grid-2">
                <div className="fc"><label>Base rate</label>{Price({ path: "charge.amount" })}</div>
                {Fld({ label: "Included guests", path: "charge.includedGuests", ph: "5", num: true })}
              </div>
            )}
            <div className={`fc ${errors.price ? 'err' : ''}`}>
              <label>Pricing tiers / demographics <span className="req">*</span></label>
              <div className="bz-lang-tabs" style={{ marginBottom: 8 }}>
                <button type="button" className={`bz-lang-tab ${lang === 'es' ? 'is-active' : ''}`} onClick={() => setLang('es')}>Español</button>
                <button type="button" className={`bz-lang-tab ${lang === 'en' ? 'is-active' : ''}`} onClick={() => setLang('en')}>English</button>
              </div>
              {(tiers.length ? tiers : [{ label: blEmpty(), price: '' }]).map((tt, i) => (
                <div className="tier-card" key={i}>
                  <div className="orow">
                    <input
                      value={asBL(tt.label)[lang]}
                      onChange={(e) => set(`charge.tiers.${i}.label.${lang}`, e.target.value)}
                      placeholder={lang === 'es' ? 'ej. Adulto' : 'e.g. Adult'}
                    />
                    <div className="pwrap"><span className="sym">{sym}</span><input type="number" value={tt.price} onChange={(e) => set(`charge.tiers.${i}.price`, e.target.value)} /></div>
                    <button type="button" className="rm" onClick={() => rmTier(i)}>×</button>
                  </div>
                  <div className="tier-lim">
                    <span className="tl-cap">Per booking</span>
                    <label>Min</label>
                    <input type="number" min={0} value={tt.min ?? ''} onChange={(e) => set(`charge.tiers.${i}.min`, e.target.value)} placeholder="0" />
                    <label>Max</label>
                    <input type="number" min={0} value={tt.max ?? ''} onChange={(e) => set(`charge.tiers.${i}.max`, e.target.value)} placeholder="—" />
                  </div>
                </div>
              ))}
              <button type="button" className="chip-add" onClick={addTier}>+ tier</button>
              <div className="hintbox" style={{ marginTop: 8 }}><Info size={14} />
                <span>Total capacity is set under <b>Scheduling &amp; Capacity</b>. Each tier's <b>min/max</b> limits how many of that type can be in a single booking — e.g. Adults min 1, max 6.</span>
              </div>
              <div className="msg">{errors.price || ''}</div>
            </div>
          </>
        ) : (
          <div className={`fc ${errors.price ? 'err' : ''}`} style={{ maxWidth: 240 }}>
            <label>Base price <span className="req">*</span></label>
            {Price({ path: "charge.amount" })}
            <div className="msg">{errors.price || ''}</div>
          </div>
        )}

        {form.type === 'rental' && (
          <>
            <div className="bz-section-title" style={{ margin: '18px 0 8px' }}>Additional fees</div>
            {fees.map((fe, i) => (
              <div className="orow" key={i}>
                <input value={fe.label} onChange={(e) => set(`charge.extraFees.${i}.label`, e.target.value)} placeholder="e.g. Cleaning fee" />
                <div className="pwrap"><span className="sym">{sym}</span><input type="number" value={fe.amount} onChange={(e) => set(`charge.extraFees.${i}.amount`, e.target.value)} /></div>
                <button type="button" className="rm" onClick={() => rmFee(i)}>×</button>
              </div>
            ))}
            <button type="button" className="chip-add" onClick={addFee}>+ add fee</button>
          </>
        )}

        {form.type === 'product' && (
          <div className="bz-grid-2" style={{ marginTop: 14 }}>
            {Fld({ label: "Inventory / stock", path: "inventory", ph: "Optional" })}
          </div>
        )}

        {isXhinolaAdmin ? (
        <div className="fc" style={{ marginTop: 6 }}>
          <label>Payment gateway</label>
          <select value={form.gateway} onChange={(e) => set('gateway', e.target.value)}>
            <option value="portal">Portal</option>
            <option value="carnet" disabled={!carnetEnabled}>Cardnet{carnetEnabled ? '' : ' (not configured)'}</option>
          </select>
          {!carnetEnabled && <div className="cap" style={{ fontSize: 12, color: '#92400E', marginTop: 6 }}>Add Merchant &amp; Terminal IDs in the business <b>Payments</b> tab to enable Cardnet.</div>}
        </div>
        ) : mode === 'edit' && (
        <div className="fc" style={{ marginTop: 6 }}>
          <label>Payment gateway</label>
          <div data-testid="gateway-readonly">{form.gateway === 'carnet' ? 'Cardnet' : 'Portal'}</div>
        </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14 }}>
          <div className={`otoggle ${form.promo ? 'on' : ''}`} onClick={() => set('promo', !form.promo)} />
          <label style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#374151' }}>Allow promo codes</label>
        </div>
      </>
    );
  };

  const SecBooking = () => {
    const par = paradigm(form.type);
    return (
      <>
        {(par === 'TIME_BASED' || par === 'EVENT_ACCESS') && (
          <div className="fc">
            <span className={`chk ${form.booking.waitlist ? 'on' : ''}`} onClick={() => set('booking.waitlist', !form.booking.waitlist)}>
              <span className="box"><Check size={12} /></span>Enable waitlist
            </span>
          </div>
        )}
        <div className="bz-grid-2">
          {Sel({ label: "Approval flow", path: "booking.approval", opts: [['instant', 'Instant booking'], ['request', 'Request approval']] })}
          {Sel({ label: "Payment requirement", path: "booking.payReq", opts: [['free', 'Free to book'], ['min_consume', 'Min consumption'], ['cc_hold', 'CC hold (security/penalty)'], ['deposit', 'Deposit / partial'], ['full', 'Full prepay']] })}
        </div>
        {['min_consume', 'cc_hold', 'deposit'].includes(form.booking.payReq) && (
          <div className="bz-grid-2">
            {Fld({ label: "Amount ($ or %)", path: "booking.payAmt", ph: "", num: true })}
            {form.booking.payReq === 'cc_hold'
              ? Sel({ label: "Hold method", path: "booking.payMethod", opts: [['cc_auth', 'Credit card auth (hold)'], ['charge_refund', 'Charge upfront & refund']] })
              : <div />}
          </div>
        )}
        <div className="bz-grid-2">
          {Sel({ label: "Cancellation policy", path: "booking.cancel", opts: [['flexible', 'Flexible'], ['moderate', 'Moderate'], ['strict', 'Strict'], ['nonref', 'Non-refundable']] })}
          {Fld({ label: "Age restriction", path: "booking.age", ph: "e.g. 18", num: true })}
        </div>
        <div className="fc">
          <label>Terms &amp; conditions</label>
          <textarea value={form.booking.terms} onChange={(e) => set('booking.terms', e.target.value)} placeholder="Optional" />
        </div>
      </>
    );
  };

  // Step-back target for the header arrow; null on the first screen of a flow,
  // where there is nowhere to go back to (use Cancel / ✕ to leave).
  const headBack =
    phase === 'subtype' ? () => setPhase('type')
    : phase === 'form' && mode === 'create' ? () => setPhase('subtype')
    : phase === 'type' && mode === 'edit' ? () => setPhase('form')
    : null;

  const secs = offerSecs(form.type);
  const activeSec = secs.find((s) => s.k === sec) ? sec : 'details';
  const secDone = (k: string) => k === 'details' ? (form.name.es || form.name.en || '').trim() !== '' : k === 'pricing' ? priceFilled(form) : false;

  return (
    <Drawer
      isOpen={isOpen}
      autoFocus
      onClose={onClose}
      size={SIZE.default}
      anchor={ANCHOR.right}
      overrides={{
        Root: { style: { zIndex: 9999 } },
        DrawerContainer: { style: { width: '480px', zIndex: 10000 } },
        DrawerBody: { style: { ...DRAWER_BODY_RESET, height: '100%' } },
        Close: { style: { display: 'none' } },
      }}
    >
      <div className="pw-drawer">
        <div className="pw-head">
          <div className="pw-head-left">
            {/* Persistent step-back, per the design: subtype → type, form → subtype
                (and, while editing, type → back to the form). */}
            {headBack && (
              <button type="button" className="pw-back" onClick={headBack} aria-label="Back"><ArrowLeft size={16} /></button>
            )}
            <div>
              <div className="pw-title">{header.t}</div>
              <div className="pw-sub">{header.s}{businesses.length === 0 ? '' : ''}</div>
            </div>
          </div>
          <button type="button" className="pw-x" onClick={onClose} aria-label="Close"><X size={16} /></button>
        </div>

        <div className="pw-body pw-form">
          {/* Phase: type — also reachable from "Change type" while editing */}
          {phase === 'type' && (
            <>
              {(mode === 'edit' && LEGACY_OFFER_TYPES.has(form.type) ? OFFER_TYPES : CREATABLE_OFFER_TYPES).length > 0 ? (
                <>
                  <p className="role-hint">What are you adding to this business's catalogue?</p>
                  <OfferPickerGrid
                    items={mode === 'edit' && LEGACY_OFFER_TYPES.has(form.type) ? OFFER_TYPES : CREATABLE_OFFER_TYPES}
                    onPick={pickType}
                  />
                </>
              ) : (
                // SAM-198 — nothing is creatable from this picker any more: appointment/rental/
                // ticket moved to Services (SAM-545), and product/subscription ("Products" /
                // "Memberships") are retired outright — the app can't book either, so offering
                // them here only invited building rows that don't work (Confluence "12.4 Services
                // admin UI", "the two-catalogue trap"). Existing rows of any of these types keep
                // opening/editing exactly as before via LEGACY_OFFER_TYPES.
                <p className="role-hint">
                  New catalogue entries are created as a <strong>Service</strong> now (appointments,
                  rentals & spaces, access & tickets) — everything the app can actually book.
                  Products and Memberships are no longer offered here for new entries; existing
                  ones stay visible and editable.
                </p>
              )}
              {/* SAM-545 — bookable offerings moved to Services; point people there
                  instead of letting them rediscover the retired cards. */}
              {!(mode === 'edit' && LEGACY_OFFER_TYPES.has(form.type)) && (
                <p className="role-hint" style={{ marginTop: '12px' }}>
                  Need something guests book by time, date or stay (an appointment, a
                  rental, or a ticketed access)? Create it as a <strong>Service</strong> from
                  the Services page instead.
                </p>
              )}
            </>
          )}

          {/* Phase: subtype */}
          {phase === 'subtype' && (
            <>
              <p className="role-hint">Pick the setup that fits — it tailors scheduling, pricing and policies.</p>
              <OfferPickerGrid items={OFFER_SUBTYPES[form.type] || []} onPick={pickSubtype} />
            </>
          )}

          {/* Phase: form */}
          {phase === 'form' && (
            <>
              <div className="pw-typebar">
                <span className="pw-typebar-label">
                  {(() => {
                    const t = typeLabel(form.type, form.subtype);
                    const sub = form.subtype ? subtypeName(form.type, form.subtype) : '';
                    // e.g. Access/Access — the subtype-aware label can equal the subtype name.
                    return !sub || sub === t ? t : `${t} · ${sub}`;
                  })()}
                </span>
                {canEditProduct && <button type="button" className="pw-typebar-change" onClick={() => setPhase('type')}>Change type</button>}
              </div>
              <div className="osec-nav">
                {secs.map((s) => (
                  <button type="button" key={s.k} className={`${activeSec === s.k ? 'on' : ''} ${secDone(s.k) ? 'done' : ''}`} onClick={() => setSec(s.k)}>
                    <span className="ck"><Check size={13} /></span>{s.n}
                  </button>
                ))}
              </div>

              {(mode === 'create' || isAdmin) && !hideBusinessSelector && activeSec === 'details' && (
                <div className="fc">
                  <label>Business</label>
                  <select value={targetBiz} onChange={(e) => setTargetBiz(e.target.value)} disabled={mode === 'edit'}>
                    <option value="">Select business…</option>
                    {lookup.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>
              )}

              {activeSec === 'details' && SecDetails()}
              {activeSec === 'avail' && SecAvail()}
              {activeSec === 'pricing' && SecPricing()}
              {activeSec === 'booking' && SecBooking()}

              {mode === 'edit' && product && canEditProduct && (
                <div className="danger-block">
                  <div className="bz-section-title" style={{ marginBottom: 10 }}>Danger zone</div>
                  {removeArmed ? (
                    <div className="danger-confirm">
                      <p>Delete <b>{form.name.es || form.name.en || 'this offer'}</b>? This cannot be undone.</p>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button type="button" className="pw-btn" style={{ flex: 1 }} onClick={() => setRemoveArmed(false)}>Cancel</button>
                        <button type="button" className="danger-btn" style={{ flex: 1 }} onClick={handleDelete} disabled={isDeleting}>
                          <Trash2 size={14} />{isDeleting ? 'Deleting…' : 'Confirm delete'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button type="button" className="danger-btn" onClick={() => setRemoveArmed(true)}><Trash2 size={14} />Delete offer</button>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="pw-foot">
          {/* Back now lives in the header arrow, so the footer is the way out. */}
          {phase === 'type' && (
            <button type="button" className="pw-btn" onClick={onClose}>Cancel</button>
          )}
          {phase === 'subtype' && (
            <button type="button" className="pw-btn" onClick={onClose}>Cancel</button>
          )}
          {phase === 'form' && (
            <>
              {mode === 'create'
                ? <button type="button" className="pw-btn" onClick={() => setPhase('subtype')}><ArrowLeft size={14} />Back</button>
                : <button type="button" className="pw-btn" onClick={onClose}>Cancel</button>}
              {canEditProduct && (
                <button type="button" className="pw-btn primary" onClick={handleSave} disabled={isSaving}>
                  {mode === 'create' ? <Plus size={14} /> : <Save size={14} />}
                  {isSaving ? 'Saving…' : mode === 'create' ? 'Create offer' : 'Save changes'}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </Drawer>
  );
};
